/**
 * Phase 125 — Tournament Organizer Activation at /arenax/host/organizer.
 *
 * Organizers are mythology accelerators — they create the recurring
 * rituals, leagues, and tension systems that turn ArenaX from a feed
 * into a city's sports rhythm.
 *
 * Captures organizer_type taxonomy, recurring_frequency, structured
 * social_links, and trust_score=0 ledger.  On success grants the
 * `create_league` capability + celebration overlay.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useArenaxCapabilities } from '@/lib/arenaxCapabilities';
import ArenaxLayout from './ArenaxLayout';
import { ArenaxWordmark } from './ArenaxWordmark';
import ArenaxActivationCelebration from './ArenaxActivationCelebration';
import {
  Trophy, ArrowLeft, ArrowRight, CheckCircle2, Loader2, Calendar,
  Instagram, Twitter, Youtube, Globe, MessageCircle,
} from 'lucide-react';

const SPORTS = [
  'Football', 'Cricket', 'Hockey', 'Badminton', 'Basketball',
  'Tennis', 'Volleyball', 'Pickleball',
];

const ORGANIZER_TYPES = [
  { id: 'league_operator', label: 'League Operator', hint: 'Recurring city leagues' },
  { id: 'tournament_host', label: 'Tournament Host', hint: 'One-off / seasonal events' },
  { id: 'weekend_community', label: 'Weekend Community', hint: 'Casual recurring meetups' },
  { id: 'corporate_league', label: 'Corporate League', hint: 'Inter-company tournaments' },
  { id: 'city_association', label: 'City Association', hint: 'Federation / club body' },
  { id: 'content_creator', label: 'Content Creator', hint: 'Creator-led leagues' },
];

const RECURRING = [
  { id: 'weekly', label: 'Weekly' },
  { id: 'bi_weekly', label: 'Bi-Weekly' },
  { id: 'monthly', label: 'Monthly' },
  { id: 'quarterly', label: 'Quarterly' },
  { id: 'seasonal', label: 'Seasonal' },
  { id: 'one_off', label: 'One-off / Custom' },
];

export default function ArenaxHostOrganizer() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { refresh: refreshCapabilities } = useArenaxCapabilities();
  const [submitting, setSubmitting] = useState(false);
  const [celebrating, setCelebrating] = useState(null);
  const [form, setForm] = useState({
    organizer_name: '',
    organization_name: '',
    organizer_type: 'league_operator',
    focus_sports: ['Football'],
    city: '',
    recurring_frequency: 'weekly',
    contact_email: user?.email || '',
    contact_phone: '',
    bio: '',
    website: '',
    social_links: {
      instagram: '',
      twitter: '',
      youtube: '',
      website: '',
      whatsapp_community: '',
    },
  });

  // Late-hydrate email when AuthContext loads
  useEffect(() => {
    if (user?.email && !form.contact_email) {
      setForm(prev => ({ ...prev, contact_email: user.email }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const updateNested = (parent, k, v) => setForm(prev => ({
    ...prev, [parent]: { ...prev[parent], [k]: v },
  }));
  const toggleSport = (sport) => setForm(prev => ({
    ...prev,
    focus_sports: prev.focus_sports.includes(sport)
      ? prev.focus_sports.filter(s => s !== sport)
      : [...prev.focus_sports, sport],
  }));

  const canSubmit = (form.organizer_name || '').trim().length > 1
    && (form.city || '').trim().length > 0;

  const submit = async () => {
    if (!user) { navigate('/arenax/auth/login'); return; }
    if (!canSubmit) { toast.error('Organizer name and city required'); return; }
    setSubmitting(true);
    try {
      await api.post('/arenax/host/organizer', form);
      // Phase 178.5 — refresh capability ledger so `host_tournament` is
      // hydrated before the user lands on /arenax/host/organizer/manage.
      try { await refreshCapabilities(); } catch (_) { /* non-fatal */ }
      setCelebrating({ city: form.city });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not activate');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return (
      <ArenaxLayout>
        <section className="max-w-md mx-auto px-5 py-20 text-center arenax-themed">
          <Trophy className="h-10 w-10 mx-auto mb-3" style={{ color: 'hsl(var(--ax-accent-cyan))' }} />
          <h1 className="text-2xl font-bold mb-2"
              style={{ color: 'hsl(var(--ax-text))', fontFamily: 'Georgia, serif' }}>
            Sign in to become an organizer
          </h1>
          <p className="text-sm mb-6" style={{ color: 'hsl(var(--ax-text-muted))' }}>
            Build the rivalries that turn into mythology.
          </p>
          <button
            data-testid="host-organizer-signin-cta"
            onClick={() => navigate('/arenax/auth/login')}
            className="px-5 py-2.5 rounded-xl text-sm font-bold"
            style={{ background: 'var(--ax-primary-gradient)', color: 'hsl(var(--ax-button-text))' }}
          >
            Sign in to ArenaX
          </button>
        </section>
      </ArenaxLayout>
    );
  }

  return (
    <ArenaxLayout>
      {celebrating && (
        <ArenaxActivationCelebration
          kind="organizer"
          city={celebrating.city}
          continueLabel="Plan your first tournament"
          onContinue={() => navigate('/arenax/host?activated=organizer')}
        />
      )}

      <section className="max-w-3xl mx-auto px-5 py-10 arenax-themed" data-testid="arenax-host-organizer-page">
        <div className="text-center mb-8 space-y-2">
          <div className="inline-flex items-center justify-center mb-1">
            <ArenaxWordmark size="md" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-light tracking-tight"
              style={{ color: 'hsl(var(--ax-text))', fontFamily: 'Georgia, "Times New Roman", serif' }}>
            Run city tournaments. Build mythology.
          </h1>
          <p className="text-sm max-w-md mx-auto" style={{ color: 'hsl(var(--ax-text-muted))' }}>
            Recurring leagues. Defended champions. Rivalries that survive a week later.
          </p>
        </div>

        <div className="arenax-card arenax-operational-form p-6 sm:p-8 space-y-8" style={{ borderRadius: 16 }}>

          {/* ---- Organizer type ---- */}
          <Section title="What kind of organizer are you?" subtitle="This shapes how players + venues discover your events.">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="host-organizer-types">
              {ORGANIZER_TYPES.map(ot => {
                const active = form.organizer_type === ot.id;
                return (
                  <button
                    key={ot.id}
                    type="button"
                    onClick={() => update('organizer_type', ot.id)}
                    data-testid={`host-organizer-type-${ot.id}`}
                    className="text-left px-3 py-3 rounded-xl border transition-all hover:-translate-y-0.5"
                    style={{
                      background: active ? 'var(--ax-primary-gradient)' : 'var(--ax-card-bg-soft)',
                      color: active ? 'hsl(var(--ax-button-text))' : 'hsl(var(--ax-text))',
                      borderColor: active ? 'transparent' : 'var(--ax-border-quiet)',
                    }}
                  >
                    <div className="text-xs font-bold leading-tight">{ot.label}</div>
                    <div className="text-[10px] mt-0.5 opacity-80 leading-tight">{ot.hint}</div>
                  </button>
                );
              })}
            </div>
          </Section>

          {/* ---- Identity ---- */}
          <Section title="Identity">
            <Field label="Organizer name">
              <input
                data-testid="host-organizer-name"
                value={form.organizer_name}
                onChange={e => update('organizer_name', e.target.value)}
                placeholder="e.g. South Bengaluru League"
                className="arenax-input w-full px-4 py-3 rounded-xl text-sm"
              />
            </Field>
            <Field label="Organization (optional)" helper="The umbrella org if different from organizer name.">
              <input
                data-testid="host-organizer-organization"
                value={form.organization_name}
                onChange={e => update('organization_name', e.target.value)}
                placeholder="South Blr Sports Pvt Ltd"
                className="arenax-input w-full px-4 py-3 rounded-xl text-sm"
              />
            </Field>
            <Field label="Focus sports">
              <div className="flex flex-wrap gap-2" data-testid="host-organizer-sports">
                {SPORTS.map(s => {
                  const active = form.focus_sports.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      data-testid={`host-organizer-sport-${s.toLowerCase().replace(/\s/g, '-')}`}
                      onClick={() => toggleSport(s)}
                      className={`arenax-chip ${active ? 'is-active' : ''} text-xs px-3 py-1.5 rounded-full transition-all`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field label="City">
              <input
                data-testid="host-organizer-city"
                value={form.city}
                onChange={e => update('city', e.target.value)}
                placeholder="Bengaluru"
                className="arenax-input w-full px-4 py-3 rounded-xl text-sm"
              />
            </Field>
          </Section>

          {/* ---- Cadence ---- */}
          <Section title="Cadence" subtitle="How often do you plan to run events?">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="host-organizer-recurring">
              {RECURRING.map(r => {
                const active = form.recurring_frequency === r.id;
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => update('recurring_frequency', r.id)}
                    data-testid={`host-organizer-recurring-${r.id}`}
                    className="text-xs font-semibold px-3 py-2.5 rounded-lg border transition-all flex items-center justify-center gap-1.5"
                    style={{
                      background: active ? 'var(--ax-primary-gradient)' : 'var(--ax-card-bg-soft)',
                      color: active ? 'hsl(var(--ax-button-text))' : 'hsl(var(--ax-text-muted))',
                      borderColor: active ? 'transparent' : 'var(--ax-border-quiet)',
                    }}
                  >
                    <Calendar className="h-3.5 w-3.5" />
                    {r.label}
                  </button>
                );
              })}
            </div>
          </Section>

          {/* ---- Contact ---- */}
          <Section title="Contact">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Contact email">
                <input
                  type="email"
                  data-testid="host-organizer-email"
                  value={form.contact_email}
                  onChange={e => update('contact_email', e.target.value)}
                  placeholder="you@email.com"
                  className="arenax-input w-full px-4 py-3 rounded-xl text-sm"
                />
              </Field>
              <Field label="Contact phone">
                <input
                  data-testid="host-organizer-phone"
                  value={form.contact_phone}
                  onChange={e => update('contact_phone', e.target.value)}
                  placeholder="+91 98765 43210"
                  className="arenax-input w-full px-4 py-3 rounded-xl text-sm"
                />
              </Field>
            </div>
          </Section>

          {/* ---- Social presence ---- */}
          <Section title="Social presence" subtitle="Where you share fixtures, results, and behind-the-scenes.">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="host-organizer-social">
              <SocialField icon={Instagram} label="Instagram"
                           value={form.social_links.instagram}
                           onChange={v => updateNested('social_links', 'instagram', v)}
                           placeholder="https://instagram.com/yourleague"
                           testId="host-organizer-instagram" />
              <SocialField icon={Twitter} label="Twitter"
                           value={form.social_links.twitter}
                           onChange={v => updateNested('social_links', 'twitter', v)}
                           placeholder="https://x.com/yourleague"
                           testId="host-organizer-twitter" />
              <SocialField icon={Youtube} label="YouTube"
                           value={form.social_links.youtube}
                           onChange={v => updateNested('social_links', 'youtube', v)}
                           placeholder="https://youtube.com/@yourleague"
                           testId="host-organizer-youtube" />
              <SocialField icon={Globe} label="Website"
                           value={form.social_links.website}
                           onChange={v => updateNested('social_links', 'website', v)}
                           placeholder="https://southblrleague.in"
                           testId="host-organizer-social-website" />
              <SocialField icon={MessageCircle} label="WhatsApp Community"
                           value={form.social_links.whatsapp_community}
                           onChange={v => updateNested('social_links', 'whatsapp_community', v)}
                           placeholder="https://chat.whatsapp.com/..."
                           testId="host-organizer-whatsapp" />
            </div>
          </Section>

          {/* ---- About ---- */}
          <Section title="About">
            <Field label="Bio" helper="Tell players and venues what kind of leagues you run.">
              <textarea
                data-testid="host-organizer-bio"
                value={form.bio}
                onChange={e => update('bio', e.target.value)}
                placeholder="We've been running the city's amateur football circuit since 2021…"
                rows={4}
                className="arenax-input w-full px-4 py-3 rounded-xl text-sm resize-none"
              />
            </Field>
          </Section>

          <div className="flex items-center justify-between pt-4 border-t"
               style={{ borderColor: 'var(--ax-border-quiet)' }}>
            <button
              type="button"
              onClick={() => navigate('/arenax/host')}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg"
              style={{ color: 'hsl(var(--ax-text-muted))' }}
              data-testid="host-organizer-back"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to hub
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={submitting || !canSubmit}
              className="inline-flex items-center gap-1.5 text-xs font-bold py-2.5 px-5 rounded-lg transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
              style={{ background: 'var(--ax-primary-gradient)', color: 'hsl(var(--ax-button-text))',
                       boxShadow: 'var(--ax-shadow-glow)' }}
              data-testid="host-organizer-submit"
            >
              {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              Activate organizer <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </section>
    </ArenaxLayout>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-base font-bold tracking-tight"
            style={{ color: 'hsl(var(--ax-text))' }}>{title}</h2>
        {subtitle && (
          <p className="text-xs mt-0.5"
             style={{ color: 'hsl(var(--ax-text-muted))' }}>{subtitle}</p>
        )}
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Field({ label, helper, children }) {
  return (
    <div>
      <label className="text-[11px] tracking-wider uppercase font-bold block mb-1.5"
             style={{ color: 'hsl(var(--ax-text-muted))' }}>
        {label}
      </label>
      {children}
      {helper && (
        <p className="text-[11px] mt-1.5" style={{ color: 'hsl(var(--ax-text-faint))' }}>{helper}</p>
      )}
    </div>
  );
}

function SocialField({ icon: Icon, label, value, onChange, placeholder, testId }) {
  return (
    <div>
      <label className="text-[11px] tracking-wider uppercase font-bold flex items-center gap-1.5 mb-1.5"
             style={{ color: 'hsl(var(--ax-text-muted))' }}>
        <Icon className="h-3 w-3" />
        {label}
      </label>
      <input
        data-testid={testId}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="arenax-input w-full px-4 py-2.5 rounded-xl text-sm"
      />
    </div>
  );
}
